import React, { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useGetInventoryVehicle } from '../../../../../api/dealership/inventory/useGetInventoryVehicle'
import LineChart from '../../../../../components/Charts/LineChart'
import {
  AsyncStatsCard,
  DateRangePicker,
  ImageGallery,
  Pill,
  VerticalTimeline,
} from '@demandlocal/components'
import { useGetInventoryVehicleMetrics } from '../../../../../api/dealership/inventory/useGetInventoryVehicleMetrics'
import dayjs from 'dayjs'
import FallbackImage from './../../../../../images/fallback_car.png'
import { useGetInventoryVehicleStats } from '../../../../../api/dealership/inventory/useGetInventoryVehicleStats'
import { mapVendorsToIcons } from '../../../../../lib/utils/mapVendorsToLogos'
import { take } from 'lodash'
import { ReactComponent as Announce } from '../../../../../images/icons/sales.svg'
import { ReactComponent as Location } from '../../../../../images/icons/location.svg'
import { ReactComponent as Car } from '../../../../../images/icons/audience_car.svg'
import { ReactComponent as Calendar } from '../../../../../images/icons/calendar.svg'
import { ReactComponent as CopyText } from '../../../../../images/icons/action_copy.svg'
import { ReactComponent as ConnectLink } from '../../../../../images/icons/connect_link_icon.svg'
import { ReactComponent as PerformanceCPLineTo } from '../../../../../images/performance/performance-cp-line-to.svg'

import { DateRange } from '../../../../../lib/types/DateRange'
import toast from 'react-hot-toast'
import classNames from 'classnames'
import { useGetSoldVehiclePath } from '../../../../../api/dealership/inventory/useGetSoldVehiclePath'
import SoldVehiclePathStep from '../../../../../components/Inventory/SoldVehiclePathStep'

const VehicleOverview = (): JSX.Element => {
  const { vin, agency_id, dealership_id } = useParams()
  const navigate = useNavigate()
  const { data } = useGetInventoryVehicle(vin)
  const [filters, setFilters] = useState<DateRange>({
    dateRange: {
      startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
      endDate: dayjs().startOf('day').format('YYYY-MM-DD'),
    },
  })

  const onDateRangeChange = (dateRange: DateRange): void => {
    setFilters((prev: DateRange) => ({
      ...prev,
      ...dateRange,
    }))
  }

  const { data: vehicleMetrics, isLoading } = useGetInventoryVehicleMetrics({
    vin: vin,
    filters: {
      start_date: filters.dateRange.startDate,
      end_date: filters.dateRange.endDate,
    },
  })

  const { data: vehicleStats, isLoading: isLoadingVehicleStats } =
    useGetInventoryVehicleStats({
      vin,
    })

  const { data: vehicleTimeline, isLoading: isLoadingTimeline } = useGetSoldVehiclePath({
    vin: data?.vin_number,
  })

  const images = data?.image_url?.match(/https:\/\/.*?(?=\|\|\|\||\|https|,https|;https|$)/) || []

  const dealerAddress = useMemo(() => {
    const { address, city, state, zip } = vehicleStats?.dealership || {}
    return [address, city, state, zip].filter(Boolean).join(', ')
  }, [vehicleStats])

  const notMarketed = useMemo(() => {
    return (
      !isLoadingVehicleStats &&
      (!vehicleStats || !vehicleStats.vendors || !vehicleStats.vendors.length)
    )
  }, [vehicleStats, isLoadingVehicleStats])

  const dealershipDisplay = useMemo(() => {
    if (
      !isLoadingVehicleStats &&
      (!vehicleStats || !vehicleStats.dealership || !vehicleStats.dealership.dealership)
    )
      return 'N/A'
    else return vehicleStats?.dealership?.dealership
  }, [vehicleStats, isLoadingVehicleStats])

  const daysOnLot = useMemo(() => {
    if (!isLoadingVehicleStats && (!vehicleStats || !vehicleStats.days_on_lot))
      return 'New Feed'
    else return vehicleStats?.days_on_lot
  }, [vehicleStats, isLoadingVehicleStats])

  const emptyCharts = useMemo(() => {
    return !isLoading && (!vehicleMetrics || !vehicleMetrics.length)
  }, [isLoading, vehicleMetrics])

  return (
    <div className={'flex flex-col gap-10 pb-8'}>
      <div className={'grid grid-cols-3 gap-5'}>
        <div className={'col-span-2'}>
          <div className={'h-[640px]'}>
            <ImageGallery
              fallbackImage={FallbackImage}
              images={images}
              hasCarousel={true}
            />
          </div>
        </div>
        <div className={'flex flex-col gap-5'}>
          <div
            className={
              'border-2 border-gainsboro-gray rounded-md p-5 flex flex-col gap-6'
            }>
            <div className="flex flex-row justify-between">
              <div className={'flex flex-col gap-2'}>
                <div className={'flex flex-row justify-start items-center gap-2'}>
                  <span className={'text-xs'}>VIN</span>
                  {!isLoadingVehicleStats && !vehicleStats?.days_on_lot && (
                    <Pill type="success">
                      <span>New</span>
                    </Pill>
                  )}
                </div>
                <span className={'text-sm font-bold'}>{data?.vin_number}</span>
              </div>
              <div className="flex py-auto items-center">
                <CopyText
                  className="cursor-pointer stroke-primary-xiketic"
                  onClick={() => {
                    navigator.clipboard.writeText(data?.vin_number ?? '')
                    toast.success('Copied VIN to clipboard', { duration: 1000 })
                  }}
                />
              </div>
            </div>
            <div className="flex flex-row justify-between">
              <div className={'flex flex-col gap-2'}>
                <span className={'text-xs'}>Stock number</span>
                <span className={'text-sm font-bold'}>{data?.stock_number}</span>
              </div>
              <div className="flex py-auto items-center">
                <CopyText
                  className="cursor-pointer stroke-primary-xiketic"
                  onClick={() => {
                    navigator.clipboard.writeText(data?.stock_number ?? '')
                    toast.success('Copied Stock number to clipboard', { duration: 1000 })
                  }}
                />
              </div>
            </div>
          </div>
          <div
            className={
              'border-2 border-gainsboro-gray rounded-md p-5 flex flex-col gap-6'
            }>
            <div className="flex flex-row gap-5 items-center">
              <div className="w-[24px]">
                <Location className="stroke-primary-xiketic" />
              </div>
              <div className={'flex flex-col gap-2'}>
                <span className={'text-xs'}>Seen at</span>
                <span className={'text-sm font-bold text-primary-persian-blue'}>{`${
                  isLoadingVehicleStats ? '' : dealerAddress || 'N/A'
                }`}</span>
              </div>
            </div>
            <div className="flex flex-row gap-5 items-center">
              <div className="w-[24px]">
                <Car />
              </div>
              <div className={'flex flex-col gap-2'}>
                <span className={'text-xs'}>Dealer</span>
                <span className={'text-sm font-bold text-primary-persian-blue'}>
                  {dealershipDisplay}
                </span>
              </div>
            </div>
            <div className="flex flex-row gap-5 items-center">
              {' '}
              <div className="w-[24px]">
                <ConnectLink className="stroke-primary-xiketic fill-primary-xiketic w-[24px]" />
              </div>
              <div className={'flex flex-col gap-2'}>
                <span className={'text-xs'}>Inventory Source</span>
                <span className={'text-sm font-bold text-primary-persian-blue'}>
                  {dealershipDisplay}
                </span>
              </div>
            </div>
            <div className="flex flex-row gap-5 items-center">
              <div className="w-[24px]">
                <Calendar />
              </div>
              <div className={'flex flex-col gap-2'}>
                <span className={'text-xs'}>Days on lot</span>
                <span className={'text-sm font-bold'}>{daysOnLot}</span>
              </div>
            </div>
            <div className="flex flex-row gap-5 items-center">
              <div className="w-[24px]">
                <Announce />
              </div>
              <div className={'flex flex-col'}>
                <span className={'text-xs'}>Marketed on</span>
                <div className={`flex flex-row gap-2 items-center max-h-8`}>
                  {!isLoadingVehicleStats && notMarketed && (
                    <span className={'text-sm'}>This vehicle has not been marketed</span>
                  )}
                  {!isLoadingVehicleStats &&
                    vehicleStats &&
                    vehicleStats.vendors &&
                    take(
                      mapVendorsToIcons(
                        vehicleStats?.vendors.map((v) => v.vendor_name),
                        'vendors',
                      ),
                      4,
                    ).map((vendor, index) => {
                      return (
                        <div
                          key={index}
                          className={
                            'flex bg-white rounded-full justify-center items-center p-1.5 w-8 h-8'
                          }>
                          {vendor}
                        </div>
                      )
                    })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className={
          'flex flex-row bg-primary-persian-blue/5 rounded-lg border-2 border-dashed border-primary-persian-blue/10 p-5'
        }>
        <div
          className={
            'flex justify-center my-auto rounded-full bg-primary-persian-blue/20 h-10 w-10'
          }>
          <Announce
            className={
              'bg-primary-persian-blue/20rounded-full my-auto stroke-primary-persian-blue h-[25px] w-[25px]'
            }
          />
        </div>
        <div className={'flex flex-col ml-5 justify-start items-center ml-2 mr-auto'}>
          <span
            className={
              'font-Spartan mr-auto text-[10px] text-primary-glaucous font-bold'
            }>
            Because you marketed this car on{' '}
            {vehicleStats?.vendors.map((v) => v.vendor_name).join(',')}
          </span>
          <span
            className={
              'font-Spartan font-medium mt-2 mr-auto text-primary-independence text-sm'
            }>
            Sell this car faster by marketing it to multiple providers
          </span>
        </div>
        <div className="flex justify-center ml-3 items-center">
          <Car />
        </div>
        <div className="justify-center ml-5 items-center my-auto">
          <span
            className={
              'font-Spartan font-bold text-primary-persian-blue cursor-pointer font-sm'
            }
            onClick={(e) => {
              e.preventDefault()
              navigate(
                `/dashboard/agency/${agency_id}/dealership/${dealership_id}/performance/campaigns/list/create`,
              )
            }}>
            Add
          </span>
        </div>
      </div>
      <div className={'flex justify-between items-center'}>
        <span className="font-bold">Marketed attributes</span>
        <DateRangePicker filters={filters} onChange={onDateRangeChange} />
      </div>
      <div className={'flex grid grid-cols-2 gap-5'}>
        <div className={'min-h-[270px] border-2 rounded-xl'}>
          <div
            className={
              'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
            }>
            <span className={classNames('', { 'opacity-40': emptyCharts })}>
              Total Impressions
            </span>
            <span className={classNames('', { 'opacity-40': emptyCharts })}>
              {!isLoading &&
                new Intl.NumberFormat('en-US').format(
                  (vehicleMetrics ?? [])
                    ?.map((metric: { impressions: number }) => metric.impressions)
                    .reduce((acc: number, cur: number) => acc + cur, 0),
                )}
            </span>
          </div>
          <div className={'h-[270px] p-2'}>
            <LineChart
              data={vehicleMetrics ?? []}
              gradient={'green-to-lemon-green'}
              yKey={'impressions'}
              isLoading={isLoading}
              isPreviousData={false}
            />
          </div>
        </div>
        <div className={'min-h-[270px] border-2 rounded-xl'}>
          <div
            className={
              'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
            }>
            <span className={classNames('', { 'opacity-40': emptyCharts })}>
              Total Clicks
            </span>
            <span className={classNames('', { 'opacity-40': emptyCharts })}>
              {!isLoading &&
                new Intl.NumberFormat('en-US').format(
                  (vehicleMetrics ?? [])
                    ?.map(
                      (metric: { impressions: number; clicks: number }) => metric.clicks,
                    )
                    .reduce((acc: number, cur: number) => acc + cur, 0),
                )}
            </span>
          </div>
          <div className={'h-[270px] p-2'}>
            <LineChart
              data={vehicleMetrics ?? []}
              gradient={'red-to-orange'}
              yKey={'clicks'}
              isLoading={isLoading}
              isPreviousData={false}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col">
        <span className="font-bold pb-5">Timeline</span>
        {isLoadingTimeline &&
          Array.from({ length: 4 }, () => 0).map((step, index, arr) => {
            return (
              <div key={index} className="w-1/4 flex flex-col  items-center">
                <AsyncStatsCard
                  title=""
                  content={() => <div className="w-1/4" />}
                  isLoading={true}
                />
                {index < arr.length - 1 && (
                  <PerformanceCPLineTo
                    className={classNames('transform rotate-90 ', {
                      '-scale-y-100': index % 2 === 0,
                    })}
                  />
                )}
              </div>
            )
          })}
        {vehicleTimeline?.map((step, index, arr) => {
          return (
            <div
              key={`${step.type}_${step.date}`}
              className="w-fit flex flex-col  items-center">
              <SoldVehiclePathStep path={step} />
              {index < arr.length - 1 && (
                <PerformanceCPLineTo
                  className={classNames('transform rotate-90', {
                    '-scale-y-100': index % 2 === 0,
                  })}
                />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default VehicleOverview
